import React, { useCallback, useMemo } from 'react';
import type { Endpoint, SearchMatch, ParsedSpec } from '../types';
import { getMethodColor, getMethodBgColor } from '../parser';
import { SchemaViewer } from './SchemaViewer';
import { useStore } from '../store';
import type { OpenAPIV3 } from 'openapi-types';

interface EndpointCardProps {
  endpoint: Endpoint;
  isSelected?: boolean;
  onSelect?: (endpoint: Endpoint) => void;
  compact?: boolean;
}

// Highlight matched text based on search matches
function HighlightedText({ 
  text, 
  matches, 
  className 
}: { 
  text: string; 
  matches?: SearchMatch[]; 
  className?: string;
}) {
  if (!matches || matches.length === 0 || !text) {
    return <span className={className}>{text}</span>;
  }

  // Find matches for this text (by value or key like 'path', 'summary', etc.)
  const relevantMatches = matches.filter(m => 
    m.value === text || 
    (m.key === 'path' && className?.includes('endpoint-path')) ||
    (m.key === 'summary' && className?.includes('endpoint-summary'))
  );

  if (relevantMatches.length === 0) {
    return <span className={className}>{text}</span>;
  }

  // Merge all indices from relevant matches
  const allIndices: [number, number][] = [];
  for (const match of relevantMatches) {
    if (match.indices) {
      for (const idx of match.indices) {
        if (idx && typeof idx[0] === 'number' && typeof idx[1] === 'number') {
          allIndices.push([idx[0], idx[1]]);
        }
      }
    }
  }
  
  if (allIndices.length === 0) {
    return <span className={className}>{text}</span>;
  }
  
  // Sort indices and merge overlapping ranges
  allIndices.sort((a, b) => a[0] - b[0]);
  const mergedIndices: Array<[number, number]> = [];
  
  for (let j = 0; j < allIndices.length; j++) {
    const idx = allIndices[j];
    if (!idx) continue;
    const start = idx[0];
    const end = idx[1];
    if (mergedIndices.length === 0 || start > (mergedIndices[mergedIndices.length - 1]?.[1] ?? -1) + 1) {
      mergedIndices.push([start, end]);
    } else {
      const last = mergedIndices[mergedIndices.length - 1];
      if (last) {
        last[1] = Math.max(last[1], end);
      }
    }
  }

  // Build highlighted text
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  
  for (let i = 0; i < mergedIndices.length; i++) {
    const range = mergedIndices[i];
    if (!range) continue;
    const start = range[0];
    const end = range[1];
    
    // Add text before match
    if (start > lastIndex) {
      parts.push(
        <span key={`text-${i}`}>{text.slice(lastIndex, start)}</span>
      );
    }
    
    // Add highlighted match
    parts.push(
      <mark key={`mark-${i}`} className="search-highlight">
        {text.slice(start, end + 1)}
      </mark>
    );
    
    lastIndex = end + 1;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(<span key="text-end">{text.slice(lastIndex)}</span>);
  }

  return <span className={className}>{parts}</span>;
}

export function EndpointCard({ endpoint, isSelected, onSelect, compact }: EndpointCardProps) {
  const [collapsedSchemas, setCollapsedSchemas] = React.useState<Set<string>>(new Set());
  const activeMatches = useStore((state) => state.activeMatches);
  const searchQuery = useStore((state) => state.searchQuery);
  const specs = useStore((state) => state.specs);
  
  // Get the raw search query for highlighting in schemas
  const rawSearchQuery = searchQuery;
  
  // Get the raw spec for resolving references
  const rawSpec = useMemo(() => {
    const spec = specs.find(s => s.id === endpoint.specId);
    return spec?.raw;
  }, [specs, endpoint.specId]);
  
  const matches = useMemo(() => 
    searchQuery ? activeMatches.get(endpoint.id) : undefined,
    [activeMatches, endpoint.id, searchQuery]
  );

  const toggleSchema = useCallback((id: string) => {
    setCollapsedSchemas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const methodColor = getMethodColor(endpoint.method);
  const methodBgColor = getMethodBgColor(endpoint.method);

  const handleClick = () => {
    onSelect?.(endpoint);
  };

  if (compact) {
    return (
      <div
        className={`endpoint-card compact ${isSelected ? 'selected' : ''}`}
        onClick={handleClick}
        role="button"
        tabIndex={0}
      >
        <span
          className="endpoint-method"
          style={{ color: methodColor, backgroundColor: methodBgColor }}
        >
          {endpoint.method.toUpperCase()}
        </span>
        <HighlightedText 
          text={endpoint.path} 
          matches={matches}
          className="endpoint-path"
        />
        {endpoint.summary && (
          <HighlightedText 
            text={endpoint.summary}
            matches={matches}
            className="endpoint-summary"
          />
        )}
        {endpoint.deprecated && <span className="endpoint-deprecated">deprecated</span>}
      </div>
    );
  }

  // Get parameters
  const pathParams = endpoint.parameters?.filter((p) => p.in === 'path') || [];
  const queryParams = endpoint.parameters?.filter((p) => p.in === 'query') || [];
  const headerParams = endpoint.parameters?.filter((p) => p.in === 'header') || [];
  const hasParams = pathParams.length > 0 || queryParams.length > 0 || headerParams.length > 0;

  // Get request body
  const requestBodyContent = endpoint.requestBody?.content;
  const requestContentType = requestBodyContent ? Object.keys(requestBodyContent)[0] : undefined;
  const requestSchema = requestContentType ? requestBodyContent[requestContentType]?.schema : undefined;
  const hasRequestBody = !!endpoint.requestBody;

  // Get 200 response only
  const response200 = endpoint.responses?.['200'] || endpoint.responses?.['201'] || endpoint.responses?.['204'];
  const response200Content = (response200 as OpenAPIV3.ResponseObject)?.content;
  const responseContentType = response200Content ? Object.keys(response200Content)[0] : undefined;
  const responseSchema = responseContentType ? response200Content[responseContentType]?.schema : undefined;
  const hasResponse = !!response200;

  const renderParams = () => {
    if (!hasParams) return null;
    
    return (
      <div className="endpoint-section">
        <h4 className="endpoint-section-title">
          
          Parameters
          {endpoint.parameters && <span className="section-badge">{endpoint.parameters.length}</span>}
        </h4>
        <div className="endpoint-params">
          {pathParams.length > 0 && (
            <div className="param-section">
              <h5 className="param-section-title">Path Parameters</h5>
              {pathParams.map((param) => {
                // Get all matches for this specific parameter
                const paramMatches = matches?.filter(m => 
                  m.key?.includes('parameters') && 
                  (m.value === param.name || m.value === param.description)
                );
                const hasMatch = (paramMatches?.length ?? 0) > 0;
                return (
                  <div key={param.name} className={`param-item ${hasMatch ? 'matched' : ''}`}>
                    <span className="param-name">
                      <HighlightedText text={param.name} matches={matches} />
                      {param.required && <span className="param-required">*</span>}
                    </span>
                    <span className="param-type">{param.schema && 'type' in param.schema ? param.schema.type : 'any'}</span>
                    {param.description && (
                      <span className="param-description">
                        <HighlightedText text={param.description} matches={matches} />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {queryParams.length > 0 && (
            <div className="param-section">
              <h5 className="param-section-title">Query Parameters</h5>
              {queryParams.map((param) => {
                const paramMatches = matches?.filter(m => 
                  m.key?.includes('parameters') && 
                  (m.value === param.name || m.value === param.description)
                );
                const hasMatch = (paramMatches?.length ?? 0) > 0;
                return (
                  <div key={param.name} className={`param-item ${hasMatch ? 'matched' : ''}`}>
                    <span className="param-name">
                      <HighlightedText text={param.name} matches={matches} />
                      {param.required && <span className="param-required">*</span>}
                    </span>
                    <span className="param-type">{param.schema && 'type' in param.schema ? param.schema.type : 'any'}</span>
                    {param.description && (
                      <span className="param-description">
                        <HighlightedText text={param.description} matches={matches} />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {headerParams.length > 0 && (
            <div className="param-section">
              <h5 className="param-section-title">Headers</h5>
              {headerParams.map((param) => {
                const paramMatches = matches?.filter(m => 
                  m.key?.includes('parameters') && 
                  (m.value === param.name || m.value === param.description)
                );
                const hasMatch = (paramMatches?.length ?? 0) > 0;
                return (
                  <div key={param.name} className={`param-item ${hasMatch ? 'matched' : ''}`}>
                    <span className="param-name">
                      <HighlightedText text={param.name} matches={matches} />
                      {param.required && <span className="param-required">*</span>}
                    </span>
                    <span className="param-type">{param.schema && 'type' in param.schema ? param.schema.type : 'any'}</span>
                    {param.description && (
                      <span className="param-description">
                        <HighlightedText text={param.description} matches={matches} />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRequestBody = () => {
    if (!hasRequestBody) return null;
    
    return (
      <div className="endpoint-section">
        <h4 className="endpoint-section-title">
          
          Request Body
        </h4>
        <div className="endpoint-body">
          {requestContentType && (
            <div className="content-type">Content-Type: <code>{requestContentType}</code></div>
          )}
          {endpoint.requestBody?.description && (
            <p className="body-description">
              <HighlightedText text={endpoint.requestBody.description} matches={matches} />
            </p>
          )}
          {requestSchema && (
            <SchemaViewer
              schema={requestSchema as OpenAPIV3.SchemaObject}
              collapsedSchemas={collapsedSchemas}
              onToggleSchema={toggleSchema}
              rawSpec={rawSpec}
              searchQuery={rawSearchQuery}
            />
          )}
        </div>
      </div>
    );
  };

  const renderResponse = () => {
    if (!hasResponse) return null;
    const resp = response200 as OpenAPIV3.ResponseObject;
    const responseCode = endpoint.responses?.['200'] ? '200' : endpoint.responses?.['201'] ? '201' : '204';
    
    return (
      <div className="endpoint-section">
        <h4 className="endpoint-section-title">
          
          Response
          <span className={`response-code-badge response-${responseCode.charAt(0)}xx`}>{responseCode}</span>
        </h4>
        <div className="endpoint-response">
          {resp.description && (
            <p className="response-description-text">
              <HighlightedText text={resp.description} matches={matches} />
            </p>
          )}
          {responseContentType && (
            <div className="content-type">Content-Type: <code>{responseContentType}</code></div>
          )}
          {responseSchema && (
            <SchemaViewer
              schema={responseSchema as OpenAPIV3.SchemaObject}
              collapsedSchemas={collapsedSchemas}
              onToggleSchema={toggleSchema}
              rawSpec={rawSpec}
              searchQuery={rawSearchQuery}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`endpoint-card full ${isSelected ? 'selected' : ''}`}>
      <div className="endpoint-header" onClick={handleClick}>
        <span
          className="endpoint-method"
          style={{ color: methodColor, backgroundColor: methodBgColor }}
        >
          {endpoint.method.toUpperCase()}
        </span>
        <HighlightedText 
          text={endpoint.path}
          matches={matches}
          className="endpoint-path"
        />
        {endpoint.specTitle && (
          <span className="endpoint-spec">{endpoint.specTitle}</span>
        )}
      </div>
      
      {endpoint.summary && (
        <h3 className="endpoint-title">
          <HighlightedText text={endpoint.summary} matches={matches} />
        </h3>
      )}
      
      {endpoint.description && (
        <p className="endpoint-description">{endpoint.description}</p>
      )}
      
      {endpoint.tags.length > 0 && (
        <div className="endpoint-tags">
          {endpoint.tags.map((tag) => (
            <span key={tag} className="endpoint-tag">{tag}</span>
          ))}
        </div>
      )}
      
      {endpoint.deprecated && (
        <div className="endpoint-deprecated-banner">
          This endpoint is deprecated
        </div>
      )}

      {/* All sections displayed in a single view with inline highlighting */}
      <div className="endpoint-sections">
        {renderParams()}
        {renderRequestBody()}
        {renderResponse()}
        
        {!hasParams && !hasRequestBody && !hasResponse && (
          <div className="endpoint-section">
            <p className="no-data">No parameters, request body, or response defined for this endpoint.</p>
          </div>
        )}
      </div>
    </div>
  );
}
